import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { brl, maskCEP } from "@/lib/format";
import { ShoppingBag, Heart, Truck, ShieldCheck, RefreshCw, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [cep, setCep] = useState("");
  const [shipping, setShipping] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    setImg(0); setQty(1); setShipping(null); setP(null);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [slug]);

  if (p === false) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#8C7A80]">Produto não encontrado.</div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-20"><div className="h-96 bg-white rounded-2xl animate-pulse" /></div>;

  const hasPromo = p.promo_price && p.promo_price < p.price;
  const price = hasPromo ? p.promo_price : p.price;
  const pixPrice = price * 0.95;

  const calcShipping = async () => {
    try {
      const { data } = await api.post("/shipping/calculate", { cep, subtotal: price * qty });
      setShipping(data);
    } catch (err) {
      toast.error("Não foi possível calcular o frete");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <nav className="text-xs text-[#8C7A80] mb-4">
        <Link to="/" className="hover:text-[#C9708B]">Início</Link> / <span className="text-[#C9708B]">{p.name}</span>
      </nav>
      <div className="grid md:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-white border border-[#F9E1E8]" data-testid="product-main-image">
            <img src={p.images?.[img] || "https://placehold.co/600x600/F9E1E8/C9708B?text=Lara+B."} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.images?.length > 1 && (
            <div className="mt-3 flex gap-2">
              {p.images.map((src, i) => (
                <button key={i} onClick={() => setImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${i===img ? 'border-[#C9708B]' : 'border-transparent'}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            {p.new_arrival && <span className="bg-[#D4B483] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Novo</span>}
            {hasPromo && <span className="bg-[#E57373] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Promoção</span>}
            {p.bestseller && <span className="bg-[#7DBE9E] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Mais vendido</span>}
          </div>
          <h1 className="font-serif font-bold text-[#4A3B40] text-3xl sm:text-4xl" data-testid="product-title">{p.name}</h1>
          <p className="text-xs text-[#8C7A80] mt-1">SKU: {p.sku}</p>

          <div className="mt-6 flex items-end gap-3">
            <span className="font-sans font-bold text-[#C9708B] text-3xl" data-testid="product-price">{brl(price)}</span>
            {hasPromo && <span className="text-[#8C7A80] line-through">{brl(p.price)}</span>}
          </div>
          <p className="text-[#7DBE9E] font-semibold mt-1">ou {brl(pixPrice)} no PIX (5% OFF)</p>
          <p className="text-xs text-[#8C7A80]">em até 6x de {brl(price/6)} sem juros</p>

          {p.stock > 0 && p.stock <= 5 && (
            <p className="mt-3 text-[#E57373] text-sm font-semibold" data-testid="low-stock-warning">
              🔥 Últimas {p.stock} unidades!
            </p>
          )}

          {/* Qty */}
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-[#F9E1E8] rounded-full">
              <button onClick={() => setQty(Math.max(1, qty-1))} className="w-10 h-10 text-[#C9708B]"><Minus className="w-4 h-4 mx-auto" /></button>
              <span className="w-10 text-center font-semibold" data-testid="product-qty">{qty}</span>
              <button onClick={() => setQty(qty+1)} className="w-10 h-10 text-[#C9708B]"><Plus className="w-4 h-4 mx-auto" /></button>
            </div>
            <button
              onClick={() => { add(p, null, qty); toast.success("Adicionado ao carrinho 💗"); }}
              disabled={p.stock <= 0}
              className="flex-1 btn-lara disabled:opacity-50"
              data-testid="product-add-to-cart"
            >
              <ShoppingBag className="w-4 h-4" /> Adicionar ao carrinho
            </button>
          </div>

          {/* Shipping calc */}
          <div className="mt-6 bg-white rounded-2xl p-4 border border-[#F9E1E8]">
            <p className="font-serif text-[#4A3B40] font-semibold mb-2 flex items-center gap-2"><Truck className="w-4 h-4" /> Calcular frete</p>
            <div className="flex gap-2">
              <input
                value={cep}
                onChange={(e) => setCep(maskCEP(e.target.value))}
                placeholder="00000-000"
                className="flex-1 border border-[#F9E1E8] rounded-full px-4 py-2 text-sm outline-none focus:border-[#C9708B]"
                data-testid="product-cep-input"
              />
              <button onClick={calcShipping} className="btn-lara-outline py-2 px-4 text-sm" data-testid="product-cep-calc">Calcular</button>
            </div>
            {shipping && (
              <div className="mt-3 space-y-1">
                {shipping.options.map((o) => (
                  <div key={o.code} className="flex justify-between text-sm py-1 border-b border-[#F9E1E8] last:border-0">
                    <span className="text-[#4A3B40]">{o.name} <span className="text-[#8C7A80]">({o.days} dias úteis)</span></span>
                    <span className={`font-semibold ${o.free ? 'text-[#7DBE9E]' : 'text-[#C9708B]'}`}>{o.free ? 'Grátis' : brl(o.price)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Trust */}
          <div className="mt-5 grid grid-cols-3 gap-3 text-center">
            <div className="flex flex-col items-center gap-1 text-xs text-[#8C7A80]"><ShieldCheck className="w-5 h-5 text-[#C9708B]" />Compra segura</div>
            <div className="flex flex-col items-center gap-1 text-xs text-[#8C7A80]"><Truck className="w-5 h-5 text-[#C9708B]" />Envio Brasil</div>
            <div className="flex flex-col items-center gap-1 text-xs text-[#8C7A80]"><RefreshCw className="w-5 h-5 text-[#C9708B]" />Troca em 7 dias</div>
          </div>

          {/* Details */}
          <div className="mt-7 space-y-3">
            <div>
              <h3 className="font-serif font-semibold text-[#4A3B40]">Descrição</h3>
              <p className="text-[#8C7A80] text-sm mt-1 leading-relaxed">{p.description}</p>
            </div>
            {p.material && (
              <div><h3 className="font-serif font-semibold text-[#4A3B40]">Material</h3><p className="text-[#8C7A80] text-sm">{p.material}</p></div>
            )}
            {p.age_range && (
              <div><h3 className="font-serif font-semibold text-[#4A3B40]">Faixa etária</h3><p className="text-[#8C7A80] text-sm">{p.age_range}</p></div>
            )}
          </div>
        </div>
      </div>

      {/* Related */}
      {p.related?.length > 0 && (
        <section className="mt-16">
          <h2 className="font-serif font-bold text-[#4A3B40] text-2xl mb-6">Você também pode gostar</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
